import { DownOutlined, EditOutlined, UploadOutlined, DownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HeaderTabs } from '.';
import { type AudiFinancingModuleSpecsFragment } from '../../../../api/fragments';
import { useAccountContext } from '../../../../components/contexts/AccountContextManager';
import { exportAudiFinancingSalesPersonCodeMappings } from '../../../../utilities/export';
import { type PermissionValues } from '../../../../utilities/hasPermissions';
import useDeleteModule from '../useDeleteModule';

export type AudiFinancingModuleActionProps = {
    module: AudiFinancingModuleSpecsFragment;
    setActiveTab: (value: HeaderTabs) => void;
    openSaleForceDrawer: () => void;
    openImportMappingsModal: () => void;
    permissions: Pick<PermissionValues, 'hasDeletePermission'>;
};

const AudiFinancingModuleAction = ({
    module,
    setActiveTab,
    openSaleForceDrawer,
    openImportMappingsModal,
    permissions,
}: AudiFinancingModuleActionProps) => {
    const { t } = useTranslation(['audiFinancingModuleDetails']);
    const { token } = useAccountContext();
    const deleteModule = useDeleteModule(module.id);

    const downloadMappings = useCallback(() => {
        exportAudiFinancingSalesPersonCodeMappings(token, module.id);
    }, [module.id, token]);

    const { hasDeletePermission } = permissions;

    const items = [
        {
            label: t('audiFinancingModuleDetails:actions.updateSalesForce'),
            key: 'updateSalesForce',
            icon: <EditOutlined />,
            onClick: () => {
                setActiveTab(HeaderTabs.SalesForce);
                openSaleForceDrawer();
            },
        },
        {
            label: t('audiFinancingModuleDetails:actions.importMappings'),
            key: 'importMappings',
            icon: <UploadOutlined />,
            onClick: () => {
                setActiveTab(HeaderTabs.SalesPersonCodeMappings);
                openImportMappingsModal();
            },
        },
        {
            label: t('audiFinancingModuleDetails:actions.exportMappings'),
            key: 'exportMappings',
            icon: <DownloadOutlined />,
            onClick: () => {
                setActiveTab(HeaderTabs.SalesPersonCodeMappings);
                downloadMappings();
            },
        },
        hasDeletePermission && {
            label: t('audiFinancingModuleDetails:actions.delete'),
            key: 'delete',
            icon: <DeleteOutlined />,
            onClick: deleteModule,
            danger: true,
        },
    ];

    return (
        <Dropdown overlay={<Menu items={items} />}>
            <Button type="primary">
                <Space>
                    <span>{t('audiFinancingModuleDetails:actions.label')}</span>
                    <DownOutlined />
                </Space>
            </Button>
        </Dropdown>
    );
};

export default AudiFinancingModuleAction;
